import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetSurveyByIdQuery,
  useLazyGetOrganizationByIdQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service.js";
import SectionProgressBar from "./components/SectionProgressBar";
import SectionContent from "./components/SectionContent.js";
import BaseButton from "@/components_v2/Button/BaseButton";
import PreviewIcon from "@/assets/icons/preview.svg?react";
import SendIcon from "@/assets/icons/send.svg?react";
import SubmissionConfirmationModal from "@/domains/survey/components/SubmissionConfirmationModal";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { SurveyType } from "@/shared/utils/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";
import { SurveySectionFormProps } from "@/features/survey/form/types";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";
import { usePageBanner } from "@/shared/contexts/PageBannerContext";
import Warning from "@/assets/icons/warning-circle.svg?react";

const SurveySectionForm = ({ surveyId, sectionId }: SurveySectionFormProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const sampleOrganizationId = searchParams.get("sampleOrganizationId");
  const currentOrganizationId = useOrganizationId();
  const { setBannerContent, clearBanner } = usePageBanner();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [getOrganizationById] = useLazyGetOrganizationByIdQuery();

  useEffect(() => {
    if (!sampleOrganizationId) {
      clearBanner();
      return;
    }

    const fetchAndSetBanner = async () => {
      const organizationData = await getOrganizationById(sampleOrganizationId);
      setBannerContent({
        title: t("survey.labels.viewingSample"),
        description: t("survey.labels.viewingSampleDescription", {
          organizationName: organizationData.data?.name || "",
        }),
        icon: {
          component: Warning,
          fill: "var(--orange-500)",
          stroke: "var(--base-white)",
        },
        titleColor: "var(--orange-500)",
        descriptionColor: "var(--orange-500)",
        backgroundColor: "#fff8f1",
      });
    };

    fetchAndSetBanner();

    return clearBanner;
  }, [sampleOrganizationId]);

  const { data: survey, isLoading: isSurveyLoading } = useGetSurveyByIdQuery(
    surveyId
      ? {
          surveyId,
          filters: {
            organization: sampleOrganizationId || currentOrganizationId,
          },
        }
      : skipToken,
  );
  const [
    getSurveyStatistics,
    {
      data: surveyStatistics,
      isLoading: isSurveyStatisticsLoading,
      isError: isSurveyStatisticsError,
    },
  ] = useLazyGetSurveyStatisticsQuery();
  const currentUser = useSelector(selectCurrentUser);
  const { canSubmit, isLoading: isLoadingPermissions } = useSurveyPermissions(
    survey,
    currentUser,
  );

  useEffect(() => {
    if (survey) {
      getSurveyStatistics({
        survey: survey.id,
        organization: sampleOrganizationId || currentOrganizationId,
      });
    }
  }, [survey]);

  if (isSurveyLoading || isSurveyStatisticsLoading || isLoadingPermissions) {
    return <BaseSpinner size="large" />;
  }

  if (!survey || isSurveyStatisticsError) {
    return <Navigate to="/" />;
  }

  return (
    <div className="survey-section-form-container flex flex-col gap-200 full-width">
      <div className="flex flex-wrap gap-200 justify-between items-center">
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.LINK}
          leadingIcon={<ArrowLeftOutlined />}
          to={`/reports/${surveyId}${location.search}`}
          type={BUTTON_TYPES.LINK}
        >
          {t("survey.labels.backToSurveyOverview", {
            surveyType: SurveyType.getLabel(survey.type),
          })}
        </BaseButton>

        {!sampleOrganizationId && (
          <div className="flex flex-wrap gap-200">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              leadingIcon={
                <BaseSvgRenderer
                  width="20"
                  height="20"
                  component={PreviewIcon}
                />
              }
              to={`/reports/${survey.id}/overview`}
              type={BUTTON_TYPES.LINK}
            >
              {t("survey.labels.previewSurvey")}
            </BaseButton>

            <BaseTooltip
              title={canSubmit ? "" : t("survey.labels.onlyAdminsCanSubmit")}
            >
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                disabled={!canSubmit}
                leadingIcon={
                  <BaseSvgRenderer
                    width="20"
                    height="20"
                    component={SendIcon}
                  />
                }
                onClick={() => setOpenConfirmationModal(true)}
              >
                {t("survey.labels.submitSurvey")}
              </BaseButton>
            </BaseTooltip>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-500">
        {surveyStatistics && <SectionProgressBar sections={surveyStatistics} />}

        <SectionContent sectionId={sectionId} />
      </div>

      {!sampleOrganizationId && (
        <SubmissionConfirmationModal
          open={openConfirmationModal}
          setOpen={setOpenConfirmationModal}
          survey={survey}
        />
      )}
    </div>
  );
};

export default SurveySectionForm;
